import { useDispatch } from 'react-redux';
import { useContext } from 'react';
import {
  RedirectReaction,
  HTMLPrinterAction,
  TriggerEndpointAction,
  Reaction,
} from '../../../services/Main/types.Component';
import ReactionController, {
  OnReactionExecuteEvent,
} from '../../../responseReactions/ReactionController';
import redirectReaction from '../../../responseReactions/redirect/redirectReaction';
import history from '../../../utils/history';
import store from '../../../store';
import { dialogWithComponentActions } from '../../../store/dialogWithComponent';
import mainService from '../../../services/Main';
import { alertsFromActionServerResponseActions } from '../../../store/alertsFromActionServerResponse';
import { metaActions } from '../../../store/meta';
import { PageContext } from '../../creational/PageLoader/PageLoader';
import { useShowConfirmDialog } from '../FormActions/useShowConfirmDialog';
import useIsSomeActionExecuting from '../FormAction/useIsSomeActionExecuting';
import { TableContext } from '../../highLevel/Table';
import { ComponentContext } from '../../creational/ComponentLoader';
import useEnqueueSnackbar from '../../../utils/hooks/useEnqueueSnackbar';
import { DialogAlertContext } from '../DialogAlert/DialogAlert';
import TableDialogWithComponentContext from '../../highLevel/Table/components/TableDialogWithComponent/TableDialogWithComponent.context';
import { useBatchActionDialog } from '../BatchActionDialog';

type HTMLPrinterActionPropsOmit = Omit<
  HTMLPrinterAction,
  'label' | 'color' | 'icon' | 'variant'
>;

interface OtherProps {
  onReactionExecute?: OnReactionExecuteEvent;
}

export const useHandleClickAction = ({
  type,
  onReactionExecute,
  ...rest
}: HTMLPrinterActionPropsOmit & OtherProps) => {
  const dispatch = useDispatch();
  const pageContext = useContext(PageContext);
  const tableContextValue = useContext(TableContext);
  const componentContext = useContext(ComponentContext);
  const enqueueSnackbar = useEnqueueSnackbar();
  const dialogAlertContext = useContext(DialogAlertContext);
  const tableDialogWithComponentContext = useContext(
    TableDialogWithComponentContext
  );
  const { setIsSomeActionExecuting } = useIsSomeActionExecuting();
  const showConfirmDialog = useShowConfirmDialog();
  const batchActionDialogContext = useBatchActionDialog();

  const onBeforeActionExecuteHandler = () => {
    const confirmDialogConfig = (rest as TriggerEndpointAction)?.confirmDialog;
    if (!confirmDialogConfig) return Promise.resolve();

    return showConfirmDialog(confirmDialogConfig);
  };

  const onActionSuccessfullyExecuted = () => {
    if (store.getState().dialogWithComponent) {
      dispatch(dialogWithComponentActions.close());
    }
    if (tableDialogWithComponentContext?.dialogWithComponent) {
      tableDialogWithComponentContext.setDialogWithComponent(null);
    }
  };

  switch (type) {
    // showForm обрабатываем выше.
    // case 'showForm':
    //   return () => console.log(type, rest);

    case 'triggerEndpoint':
      return async () => {
        await onBeforeActionExecuteHandler();

        setIsSomeActionExecuting(true);

        mainService
          .makeActionRequest((rest as TriggerEndpointAction).requestConfig)
          .then(
            ({
              payload,
              snackbar,
              dialogAlert,
              preventSuccessResponseReactionReasons,
            }) => {
              if (snackbar) enqueueSnackbar(snackbar.text, snackbar.options);
              if (dialogAlert) dialogAlertContext.setDialogState(dialogAlert);

              const alertsId = `${componentContext.businessComponentId}_${componentContext.id}`;

              if (
                preventSuccessResponseReactionReasons &&
                Array.isArray(preventSuccessResponseReactionReasons)
              ) {
                dispatch(
                  alertsFromActionServerResponseActions.setForPage({
                    id: alertsId,
                    alerts: preventSuccessResponseReactionReasons,
                  })
                );
                dispatch(metaActions.setIsSomeActionExecuting(false));
                return;
              }

              if (
                store.getState().alertsFromActionServerResponse[alertsId]
                  ?.length > 0
              ) {
                dispatch(alertsFromActionServerResponseActions.clear(alertsId));
              }

              onActionSuccessfullyExecuted();

              new ReactionController(
                (rest as TriggerEndpointAction).successResponseReaction,
                pageContext as PageContext,
                enqueueSnackbar,
                payload,
                tableContextValue,
                tableDialogWithComponentContext,
                batchActionDialogContext
              ).execute(onReactionExecute);
            }
          )
          .catch((error) => {
            if (error.response?.status === 422) {
              if (error.response?.data?.snackbar) {
                enqueueSnackbar(
                  error.response?.data?.snackbar.text,
                  error.response?.data?.snackbar.options
                );
              }

              if (error.response?.data?.dialogAlert) {
                if (error.response?.data?.dialogAlert)
                  dialogAlertContext.setDialogState(
                    error.response.data.dialogAlert
                  );
              }

              if ((rest as TriggerEndpointAction).validationErrorReaction) {
                new ReactionController(
                  (rest as TriggerEndpointAction)
                    .validationErrorReaction as Reaction,
                  pageContext as PageContext,
                  enqueueSnackbar,
                  error.response,
                  tableContextValue,
                  tableDialogWithComponentContext,
                  batchActionDialogContext
                ).execute(onReactionExecute);
              }
            }

            console.log('Ошибка', error);
          })
          .finally(() => {
            // Добавляли с целью отмены блокировок кнопок в асинхронных модалках
            // Потенциально может привести к проблемам, так как не знаем как
            // повлияет на другие части системы
            setIsSomeActionExecuting(false);
          });
      };

    case 'redirect':
      return () => {
        onActionSuccessfullyExecuted();

        redirectReaction(rest as RedirectReaction);
      };

    case 'goBack':
      return () => {
        onActionSuccessfullyExecuted();

        history.goBack();
      };

    default:
      return () => console.log('CLICK CLOCK');
  }
};
